import type { DripDashboardSnapshot } from '../types';
import { SITE_DRIP_INNER } from '../../../constants/siteUi';
import { DripSection } from './DripSection';

function alertRing(sev: DripDashboardSnapshot['alerts'][0]['severity']) {
  if (sev === 'critical') return 'border-rose-400/45 bg-rose-950/30';
  if (sev === 'warning') return 'border-amber-400/40 bg-amber-950/25';
  return 'border-sky-400/35 bg-sky-950/20';
}

export function DripAlertsBar({ alerts }: Pick<DripDashboardSnapshot, 'alerts'>) {
  return (
    <DripSection
      id="drip-alerts"
      title="Alerts & system health"
      description="Operational signals from ESP, webhooks, and DNS — surface Slack/PagerDuty in production."
    >
      <ul className="space-y-2" role="list">
        {alerts.map((a) => (
          <li
            key={a.id}
            className={`rounded-xl border px-4 py-3 text-sm ring-1 ring-white/[0.05] ${alertRing(a.severity)}`}
            role="status"
          >
            <p className="font-semibold text-[var(--home-text-on-dark)]">{a.title}</p>
            <p className="mt-1 text-xs leading-relaxed text-[var(--home-muted-on-dark)]">{a.detail}</p>
          </li>
        ))}
      </ul>
    </DripSection>
  );
}

export function DripAudiencePanel({ segmentRules }: Pick<DripDashboardSnapshot, 'segmentRules'>) {
  return (
    <DripSection
      id="drip-audience"
      title="Audience & segmentation"
      description="Dynamic filters, tags, behavioral cohorts, and saved segments — backed by your warehouse when wired."
    >
      <ul className="space-y-2">
        {segmentRules.map((r) => (
          <li key={r.id} className={`${SITE_DRIP_INNER} flex items-center justify-between gap-3 px-3 py-2`}>
            <span className="text-sm text-[var(--home-text-on-dark)]">{r.label}</span>
            <span
              className={`rounded-md px-2 py-0.5 font-mono text-[0.6rem] uppercase tracking-wide ring-1 ${
                r.active ? 'bg-emerald-500/15 text-emerald-200 ring-emerald-400/25' : 'bg-white/5 text-[var(--home-muted-on-dark)] ring-white/10'
              }`}
            >
              {r.active ? 'On' : 'Off'}
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-4 flex flex-wrap gap-2">
        <button type="button" className="rounded-lg bg-[var(--home-miami-blue)]/25 px-3 py-2 text-[0.65rem] font-semibold uppercase tracking-wide text-[var(--home-miami-blue)] ring-1 ring-[var(--home-miami-blue)]/30">
          New saved segment
        </button>
        <button type="button" className="rounded-lg border border-white/12 bg-white/[0.04] px-3 py-2 text-[0.65rem] font-semibold uppercase tracking-wide text-[var(--home-text-on-dark)]">
          Lead scoring rules
        </button>
      </div>
    </DripSection>
  );
}

const ACT_DOT: Record<DripDashboardSnapshot['activities'][0]['type'], string> = {
  open: 'bg-sky-400',
  click: 'bg-violet-400',
  reply: 'bg-amber-300',
  unsubscribe: 'bg-rose-400',
  conversion: 'bg-emerald-400',
  failure: 'bg-rose-500',
};

export function DripActivityFeed({ activities }: Pick<DripDashboardSnapshot, 'activities'>) {
  return (
    <DripSection
      id="drip-activity"
      title="Live activity"
      description="Socket-driven feed in production — UTC timestamps below are illustrative."
    >
      <ul className="max-h-[22rem] space-y-3 overflow-y-auto pr-1" aria-live="polite">
        {activities.map((a) => (
          <li key={a.id} className={`${SITE_DRIP_INNER} flex gap-3 p-3`}>
            <div
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-white/15 to-white/5 font-display text-[0.65rem] font-bold text-[var(--home-text-on-dark)] ring-1 ring-white/10"
              aria-hidden
            >
              {a.actorInitials}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`inline-block h-2 w-2 rounded-full ${ACT_DOT[a.type]}`} title={a.type} />
                <span className="text-sm font-medium text-[var(--home-text-on-dark)]">{a.actorName}</span>
                <span className="font-mono text-[0.65rem] text-[var(--home-muted-on-dark)]">
                  {new Date(a.at).toLocaleString()}
                </span>
              </div>
              <p className="mt-1 text-sm text-[var(--home-muted-on-dark)]">{a.message}</p>
            </div>
          </li>
        ))}
      </ul>
    </DripSection>
  );
}

export function DripAbTestPanel({ abTests }: Pick<DripDashboardSnapshot, 'abTests'>) {
  return (
    <DripSection
      id="drip-ab"
      title="A/B experiments"
      description="Bayesian or frequentist winner selection — confidence requires sufficient sample size."
    >
      <div className="space-y-4">
        {abTests.map((t) => (
          <div key={t.id} className={`${SITE_DRIP_INNER} p-4`}>
            <p className="font-display text-[0.62rem] font-bold uppercase tracking-[0.12em] text-[var(--home-miami-blue)]">
              {t.campaignName}
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {t.variants.map((v) => (
                <div
                  key={v.id}
                  className={`rounded-lg border px-3 py-2 ring-1 ${
                    v.id === t.winnerVariantId
                      ? 'border-emerald-400/40 bg-emerald-500/10 ring-emerald-400/20'
                      : 'border-white/10 bg-white/[0.03] ring-white/[0.04]'
                  }`}
                >
                  <p className="text-sm font-semibold text-[var(--home-text-on-dark)]">{v.name}</p>
                  <p className="mt-1 font-mono text-xs text-[var(--home-muted-on-dark)]">
                    CVR {v.conversionRatePct.toFixed(2)}% · {v.conversions}/{v.sent.toLocaleString()} conv.
                  </p>
                </div>
              ))}
            </div>
            <p className="mt-3 text-sm text-[var(--home-muted-on-dark)]">
              Winner:{' '}
              <span className="font-medium text-[var(--home-text-on-dark)]">
                {t.winnerVariantId ? t.variants.find((x) => x.id === t.winnerVariantId)?.name : 'Undetermined'}
              </span>
              {t.liftPct != null ? (
                <span className="ml-2 font-mono text-emerald-300">+{t.liftPct.toFixed(1)}% lift</span>
              ) : null}
              {t.confidencePct != null ? (
                <span className="ml-2 font-mono text-[var(--home-muted-on-dark)]">{t.confidencePct}% confidence</span>
              ) : null}
            </p>
          </div>
        ))}
      </div>
    </DripSection>
  );
}

export function DripAiRecommendations({ aiRecommendations }: Pick<DripDashboardSnapshot, 'aiRecommendations'>) {
  return (
    <DripSection
      id="drip-ai"
      title="AI recommendations"
      description="Heuristics + model suggestions — validate before auto-apply in production."
    >
      <ul className="space-y-2">
        {aiRecommendations.map((r) => (
          <li key={r.id} className="rounded-xl border border-white/[0.08] bg-gradient-to-br from-[var(--home-miami-blue)]/10 to-transparent p-4 ring-1 ring-white/[0.05]">
            <div className="flex items-start justify-between gap-2">
              <p className="font-semibold text-[var(--home-text-on-dark)]">{r.title}</p>
              <span
                className={`shrink-0 rounded-md px-2 py-0.5 font-mono text-[0.58rem] uppercase tracking-wide ring-1 ${
                  r.impact === 'high'
                    ? 'bg-rose-500/15 text-rose-100 ring-rose-400/25'
                    : r.impact === 'medium'
                      ? 'bg-amber-500/15 text-amber-100 ring-amber-400/25'
                      : 'bg-white/10 text-[var(--home-muted-on-dark)] ring-white/12'
                }`}
              >
                {r.impact}
              </span>
            </div>
            <p className="mt-2 text-sm leading-relaxed text-[var(--home-muted-on-dark)]">{r.detail}</p>
          </li>
        ))}
      </ul>
    </DripSection>
  );
}

export function DripSettingsIntegrations({ integrations }: Pick<DripDashboardSnapshot, 'integrations'>) {
  return (
    <DripSection
      id="drip-settings"
      title="Settings & integrations"
      description="Secrets via vault; rotate API keys; enforce admin-only mutations with audit trails."
    >
      <ul className={`divide-y divide-white/[0.06] ${SITE_DRIP_INNER}`}>
        {integrations.map((i) => (
          <li key={i.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
            <div>
              <p className="font-medium text-[var(--home-text-on-dark)]">{i.name}</p>
              {i.detail ? <p className="text-xs text-[var(--home-muted-on-dark)]">{i.detail}</p> : null}
            </div>
            <span
              className={`rounded-full px-3 py-0.5 text-[0.62rem] font-semibold uppercase tracking-wide ring-1 ${
                i.connected ? 'bg-emerald-500/15 text-emerald-200 ring-emerald-400/25' : 'bg-white/5 text-[var(--home-muted-on-dark)] ring-white/10'
              }`}
            >
              {i.connected ? 'Connected' : 'Not connected'}
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs text-[var(--home-muted-on-dark)]">
        Security: role-gated routes, audit logging, rate limits on public signup endpoints, idempotent webhooks with
        replay protection.
      </p>
    </DripSection>
  );
}
